import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export type CegidJobName = 'grand-livre' | 'invoice-statuses' | 'retry-errors' | 'daily-digest'
type CegidJobStatus = 'success' | 'partial' | 'error'

interface CegidJobRunCounts {
  total?: number
  processed?: number
  ok?: number
  ko?: number
  skipped?: number
  paymentRegistered?: number
  imported?: number
}

export interface CegidJobRun {
  jobName: CegidJobName
  trigger: 'cron' | 'manual'
  triggeredBy?: string
  startedAt: string
  finishedAt?: string
  durationMs?: number
  status: CegidJobStatus
  counts: CegidJobRunCounts
  errors: Array<{ ref: string; code: string; message: string }>
  crashMessage?: string
}

export interface CegidInvoiceError {
  invoiceUuid: string
  name: string | null
  companyName: string | null
  invoiceDate: string | null
  paymentState: string | null
  cegidStatus: string | null
  status: string
  errorCode: string | null
  errorMessage: string | null
  errorAt: string | null
  retryCount: number
}

type CegidJobProgressStatus = 'running' | 'success' | 'partial' | 'error' | 'cancelled'

export interface CegidJobProgress {
  runId: string
  jobName: CegidJobName
  status: CegidJobProgressStatus
  processed: number
  total: number
  counts?: Record<string, number>
  startedAt: string
  finishedAt?: string
  message?: string
}

export interface RetryInvoiceResult {
  invoiceUuid: string
  result: 'resolved_already_paid' | 'payment_registered' | 'payment_failed' | 'still_unresolved' | 'not_found'
  odooPaymentState?: string | null
  etatLettrage?: string
  retryCount?: number
  error?: string
}

export const useCegidImportsAdminStore = defineStore('cegidImportsAdmin', () => {
  const suiteStore = useSuiteStore()

  const runs = ref<CegidJobRun[]>([])
  const errors = ref<CegidInvoiceError[]>([])
  const isLoadingRuns = ref(false)
  const isLoadingErrors = ref(false)

  async function fetchRuns(opts: { limit?: number; job?: CegidJobName } = {}): Promise<CegidJobRun[]> {
    isLoadingRuns.value = true
    try {
      const qs = new URLSearchParams()
      if (opts.limit) qs.set('limit', String(opts.limit))
      if (opts.job) qs.set('job', opts.job)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      const res = await suiteStore.suiteFetch(`admin/cegid/runs${suffix}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        runs.value = ((await res.json()) as CegidJobRun[]) ?? []
        return runs.value
      }
    } catch (e) {
      console.error('Erreur récupération runs CEGID', e)
    } finally {
      isLoadingRuns.value = false
    }
    return []
  }

  async function fetchErrors(): Promise<CegidInvoiceError[]> {
    isLoadingErrors.value = true
    try {
      const res = await suiteStore.suiteFetch('admin/cegid/errors', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        errors.value = ((await res.json()) as CegidInvoiceError[]) ?? []
        return errors.value
      }
    } catch (e) {
      console.error('Erreur récupération factures en erreur CEGID', e)
    } finally {
      isLoadingErrors.value = false
    }
    return []
  }

  async function runGrandLivreSync(dateFrom: string, dateTo: string): Promise<{ ok: boolean; data?: unknown; message?: string }> {
    return postJob('admin/cegid/jobs/grand-livre', { dateFrom, dateTo })
  }

  async function runPaymentSync(
    dateFrom: string,
    dateTo: string,
    refPrefix?: string,
    retryErrors?: boolean,
  ): Promise<{ ok: boolean; data?: unknown; message?: string }> {
    return postJob('admin/cegid/jobs/invoice-statuses', {
      dateFrom,
      dateTo,
      ...(refPrefix ? { refPrefix } : {}),
      ...(retryErrors ? { retryErrors: true } : {}),
    })
  }

  async function postJob(path: string, body: Record<string, unknown>): Promise<{ ok: boolean; data?: unknown; message?: string }> {
    try {
      const res = await suiteStore.suiteFetch(path, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        return { ok: true, data: await res.json() }
      }
      let message = 'Échec du lancement du job'
      if (res && typeof res !== 'boolean') {
        try {
          const b = await res.json()
          if (b?.message) message = Array.isArray(b.message) ? b.message.join(', ') : b.message
        } catch {
          // ignore
        }
      }
      return { ok: false, message }
    } catch (e) {
      return { ok: false, message: (e as Error).message }
    }
  }

  async function cancelJob(runId: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`admin/cegid/jobs/${runId}/cancel`, {
      method: 'POST',
      credentials: 'include',
    })
    return !!(res && typeof res !== 'boolean' && res.ok)
  }

  async function getActiveJob(job: CegidJobName): Promise<CegidJobProgress | null> {
    const res = await suiteStore.suiteFetch(`admin/cegid/jobs/active?job=${job}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const j = (await res.json()) as CegidJobProgress | { status: 'idle' }
      return j && j.status !== 'idle' ? (j as CegidJobProgress) : null
    }
    return null
  }

  async function getProgress(runId: string): Promise<CegidJobProgress | null> {
    const res = await suiteStore.suiteFetch(`admin/cegid/jobs/${runId}/progress`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as CegidJobProgress
    }
    return null
  }

  async function retryInvoice(invoiceUuid: string): Promise<RetryInvoiceResult | null> {
    const res = await suiteStore.suiteFetch(`admin/cegid/errors/${invoiceUuid}/retry`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as RetryInvoiceResult
    }
    return null
  }

  return {
    runs,
    errors,
    isLoadingRuns,
    isLoadingErrors,
    fetchRuns,
    fetchErrors,
    retryInvoice,
    runGrandLivreSync,
    runPaymentSync,
    cancelJob,
    getActiveJob,
    getProgress,
  }
})
